/**
 * DQCategoryDrawerMount - Standalone Mount for DQ Category Drawer
 * ============================================================================
 * This component provides a way to mount the DataQualityCategoryDrawer
 * independently of the main React app. It's used when Data Quality results
 * are rendered by vanilla JS (standalone mode) but we still want the
 * React drawer functionality.
 *
 * Usage from vanilla JS:
 * ```js
 * // Mount the drawer (call once when DQ results are displayed)
 * window.mountDQCategoryDrawer(jobId);
 *
 * // Later, dispatch events to open it
 * dispatchDQCategoryClick('information_content', 85);
 * ```
 *
 * @version 1.0.0
 * @created 2026-01-28
 * ============================================================================
 */

import React, { useEffect, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { DataQualityCategoryDrawer } from './DataQualityCategoryDrawer';
import { useDQCategoryDrawer } from './useDQCategoryDrawer';
import type { DQCategoryClickEventDetail } from '../../types/dataQualityCategory';
import { isValidCategoryId } from '../../types/dataQualityCategory';

// ============================================================================
// Inner Component
// ============================================================================

interface DQCategoryDrawerInnerProps {
  jobId: string;
}

const DQCategoryDrawerInner: React.FC<DQCategoryDrawerInnerProps> = ({ jobId }) => {
  const {
    isOpen,
    categoryDetail,
    loading,
    error,
    openDrawer,
    closeDrawer,
  } = useDQCategoryDrawer(jobId);

  // Handle category click events from vanilla JS
  const handleCategoryClick = useCallback((event: CustomEvent<DQCategoryClickEventDetail>) => {
    const { categoryId } = event.detail;
    if (isValidCategoryId(categoryId)) {
      openDrawer(categoryId);
    }
  }, [openDrawer]);

  // Listen for dq-category-click events
  useEffect(() => {
    window.addEventListener('dq-category-click', handleCategoryClick as EventListener);
    return () => {
      window.removeEventListener('dq-category-click', handleCategoryClick as EventListener);
    };
  }, [handleCategoryClick]);

  return (
    <DataQualityCategoryDrawer
      isOpen={isOpen}
      onClose={closeDrawer}
      categoryDetail={categoryDetail}
      loading={loading}
      error={error}
    />
  );
};

// ============================================================================
// Mount Function
// ============================================================================

let drawerRoot: Root | null = null;
let drawerContainer: HTMLDivElement | null = null;

/**
 * Mount the DQ Category Drawer as a standalone React component
 * Call this when Data Quality results are displayed
 *
 * @param jobId - The Data Quality job ID
 */
export function mountDQCategoryDrawer(jobId: string): void {
  // Clean up any existing mount
  unmountDQCategoryDrawer();

  // Create container element
  drawerContainer = document.createElement('div');
  drawerContainer.id = 'dq-category-drawer-root';
  drawerContainer.setAttribute('data-job-id', jobId);
  document.body.appendChild(drawerContainer);

  // Create React root and render
  drawerRoot = createRoot(drawerContainer);
  drawerRoot.render(<DQCategoryDrawerInner jobId={jobId} />);
}

/**
 * Unmount the DQ Category Drawer
 * Call this when leaving the Data Quality page
 */
export function unmountDQCategoryDrawer(): void {
  if (drawerRoot) {
    drawerRoot.unmount();
    drawerRoot = null;
  }

  if (drawerContainer && drawerContainer.parentNode) {
    drawerContainer.parentNode.removeChild(drawerContainer);
    drawerContainer = null;
  }
}

// ============================================================================
// Global Exports
// ============================================================================

// Extend Window interface for global functions
declare global {
  interface Window {
    mountDQCategoryDrawer?: typeof mountDQCategoryDrawer;
    unmountDQCategoryDrawer?: typeof unmountDQCategoryDrawer;
  }
}

// Make mount/unmount functions available globally for vanilla JS
if (typeof window !== 'undefined') {
  window.mountDQCategoryDrawer = mountDQCategoryDrawer;
  window.unmountDQCategoryDrawer = unmountDQCategoryDrawer;
}

export default DQCategoryDrawerInner;
